
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, parseISO } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(dateString: string) {
  const date = parseISO(dateString)
  return format(date, 'MMMM d, yyyy')
}

export function validateEmail(email: string): boolean {
  // Enhanced email validation regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

// Add a utility function to sanitize user input
export function sanitizeInput(input: string): string {
  // Enhanced sanitization to prevent XSS
  return input
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
    .replace(/`/g, "&#x60;")
    .replace(/\$/g, "&#36;");
}

// Add Content Security Policy headers
export function setupCSP(): void {
  if (typeof document !== 'undefined') {
    // This is just for documentation - actual CSP headers should be set on the server
    console.log('Content Security Policy should be configured on the server');
  }
}

// Add a utility function to initialize AdSense with the specified client ID
export function initializeAdSense(): void {
  if (typeof window === 'undefined') return;
  
  try {
    // Check if we're in production before adding AdSense
    const isProd = process.env.NODE_ENV === 'production' || 
                   window.location.hostname !== 'localhost' && 
                   window.location.hostname !== '127.0.0.1';
    
    if (isProd) {
      const script = document.createElement('script');
      script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1993091028153041";
      script.async = true;
      script.crossOrigin = "anonymous";
      
      const existingScript = document.querySelector('script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]');
      if (!existingScript) {
        document.head.appendChild(script);
        console.log('AdSense script added to page');
      }
    } else {
      console.log('AdSense not initialized in development environment');
    }
  } catch (err) {
    console.error('Error initializing AdSense:', err);
  }
}

// Detect user device and platform for better responsiveness
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  
  const width = window.innerWidth;
  
  if (width < 768) {
    return 'mobile';
  } else if (width < 1024) {
    return 'tablet';
  } else {
    return 'desktop';
  }
}

// Safe localStorage access with error handling
export function safeLocalStorage(key: string, value?: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    
    if (value !== undefined) {
      localStorage.setItem(key, value);
      return value;
    } else {
      return localStorage.getItem(key);
    }
  } catch (error) {
    console.error('localStorage error:', error);
    return null;
  }
}

// Check if the app is running in development mode
export function isDevelopment(): boolean {
  return process.env.NODE_ENV === 'development' || 
         window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1';
}

// Check if the app is running in production mode
export function isProduction(): boolean {
  return !isDevelopment();
}
